import { useEffect, useRef } from 'react';
import './ParticlesBg.css';

const PARTICLE_COUNT = 72;
const MAX_DISTANCE = 130;
const SPEED = 0.38;
const ATTRACT_RADIUS = 120;
const ATTRACT_PULL = 0.045;
const MAX_SPEED = 1.8;

export default function ParticlesBg() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const ptsRef = useRef([]);
  const mouseRef = useRef({ x: -1, y: -1 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const onMouseMove = (e) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };
    const onMouseLeave = () => {
      mouseRef.current.x = -1;
      mouseRef.current.y = -1;
    };
    window.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseleave', onMouseLeave);

    // Init particles
    ptsRef.current = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * SPEED,
      vy: (Math.random() - 0.5) * SPEED,
      r: Math.random() * 1.2 + 0.8,
    }));

    const getAccent = () => {
      return document.body.classList.contains('light')
        ? '13,110,189'
        : '56,189,248';
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const pts = ptsRef.current;
      const accent = getAccent();

      const { x: mx, y: my } = mouseRef.current;
      const mouseActive = mx >= 0 && my >= 0;

      // Move
      pts.forEach(p => {
        if (mouseActive) {
          const dx = mx - p.x;
          const dy = my - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < ATTRACT_RADIUS && dist > 8) {
            p.vx += (dx / dist) * ATTRACT_PULL;
            p.vy += (dy / dist) * ATTRACT_PULL;
            const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
            if (speed > MAX_SPEED) {
              p.vx = (p.vx / speed) * MAX_SPEED;
              p.vy = (p.vy / speed) * MAX_SPEED;
            }
          }
        }
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width)  p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      });

      // Lines
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < MAX_DISTANCE) {
            const alpha = (0.18 * (1 - dist / MAX_DISTANCE)).toFixed(3);
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${accent},${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }

      // Dots
      pts.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${accent},0.5)`;
        ctx.fill();
      });

      // Cursor attract indicator
      if (mouseActive) {
        ctx.beginPath();
        ctx.arc(mx, my, 5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${accent},0.4)`;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="particles-canvas"
      aria-hidden="true"
    />
  );
}
